// src/components/HostProfileCard.js
import React, { useEffect, useState, useMemo, useRef } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Divider,
  Modal,
  TextField,
  Stack,
  Select,
  InputLabel,
  OutlinedInput,
  Chip,
  MenuItem,
  FormControl,
  FormControlLabel,
  Checkbox,
  IconButton,
  Snackbar,
  Alert
} from '@mui/material';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { http } from "../lib/axios";
import {
  ART_TYPE_OPTIONS,
  VENUE_TYPE_OPTIONS,
  LIGHTING_ENVIRONMENT_OPTIONS,
  ART_EVENT_OPTIONS,
  FOOT_TRAFFIC_OPTIONS,
  DISPLAY_AVAILABILITY_OPTIONS
} from '../utils/hostProfileOptions';
import { EngagementSummary } from "../components/EngagementSummary";
import CollapsibleComments from "../components/CollapsibleComments";
import ProfileImageManager from "./ProfileImageManager";
import AgreementAcceptModal from "../components/AgreementAcceptModal";
import { getAgreementStatus } from "../lib/apiAgreements";
import { useChat } from '../context/ChatContext';

// Normalize any absolute media URL to a relative one so CRA proxy handles it.
function toRelativeMedia(url) {
  if (!url) return url;
  if (url.startsWith('data:') || url.startsWith('blob:')) return url;
  if (url.startsWith('/media/')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) {
    // strip scheme+host; keep path/query/hash
    return url.replace(/^https?:\/\/[^/]+/, '');
  }
  const trimmed = url.replace(/^\.?\/*/, ''); // remove leading "./" or "/"
  return trimmed.startsWith('media/') ? `/${trimmed}` : `/media/${trimmed}`;
}

function ExpandableTextSection({ title, value, open, onToggle, children }) {
  return (
    <Box sx={{ mb: 1.5 }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 0.5,
          borderBottom: '1px solid rgba(17,24,39,0.18)',
          cursor: 'pointer',
        }}
        onClick={onToggle}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
          {open ? <RemoveIcon fontSize="small" /> : <AddIcon fontSize="small" />}
        </Box>
      </Box>
      {open && (
        children || (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mt: 1, whiteSpace: 'pre-line', lineHeight: 1.6 }}
          >
            {value || '–'}
          </Typography>
        )
      )}
    </Box>
  );
}

export default function HostProfileCard({ user, isSelf = false, onStartChat }) {
  const { launchChat } = useChat();
  const MAX_VISIBLE_SLIDER_DOTS = 12;
  const sliderRef = useRef(null);
  const isAuthenticated = !!localStorage.getItem('access_token');
  const profileOwnerUserId = user?.user?.id || user?.user_id || null;
  const chatTarget = useMemo(() => {
    if (!profileOwnerUserId) return null;
    return {
      id: profileOwnerUserId,
      user_id: profileOwnerUserId,
      first_name: user?.first_name || '',
      last_name: user?.last_name || '',
      username: user?.username || user?.user?.username || '',
      email: user?.email || user?.user?.email || '',
      role: user?.role || user?.user?.role || 'host',
      primary_profile_image: user?.primary_profile_image || null,
      profile_images: Array.isArray(user?.profile_images) ? user.profile_images : [],
    };
  }, [profileOwnerUserId, user]);
  const handleMessageClick = () => {
    const startChat = onStartChat || launchChat;
    if (startChat && chatTarget) startChat(chatTarget);
  };
  const [open, setOpen] = useState(false);
  const [showAgreementModal, setShowAgreementModal] = useState(false);
  const [agreementAccepted, setAgreementAccepted] = useState(null);
  const [agreementCheckError, setAgreementCheckError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    venueDescription: false,
    displaySpace: false,
  });

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    address: '',
    venue_type: '',
    venue_description: '',
    display_space_details: '',
    lighting_environment_control: [],
    art_preferences: [],
    past_art_events: [],
    foot_traffic_estimate: '',
    artwork_display_availability: '',
    staffing_during_open_hours: false,
    curation_philosophy: '',
    hosting_motivation: '',
    hosting_outcome_goals: '',
    venue_images: []
  });

  // Shared profile images (per user, like ArtistProfileCard)
  const [profileImages, setProfileImages] = useState([]); // [{id, url, is_primary, ...}]

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Generic fullscreen viewer for BOTH profile + venue images
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerImages, setViewerImages] = useState([]); // [{src, caption}]
  const [viewerIndex, setViewerIndex] = useState(0);
  const [sliderDotIndex, setSliderDotIndex] = useState(0);

  const openViewerWithImages = (imgs, idx = 0) => {
    if (!Array.isArray(imgs) || !imgs.length) return;
    setViewerImages(imgs);
    setViewerIndex(idx);
    setViewerOpen(true);
  };
  const closeViewer = () => setViewerOpen(false);

  // Hydrate host form data from user
  useEffect(() => {
    if (user) {
      setFormData(prev => ({
        ...prev,
        first_name: user.first_name || '',
        last_name: user.last_name || '',
        email: user.email || '',
        address: user.address || '',
        venue_type: user.venue_type || '',
        venue_description: user.venue_description || '',
        display_space_details: user.display_space_details || '',
        lighting_environment_control: user.lighting_environment_control || [],
        art_preferences: user.art_preferences || [],
        past_art_events: user.past_art_events || [],
        foot_traffic_estimate: user.foot_traffic_estimate || '',
        artwork_display_availability: user.artwork_display_availability || '',
        staffing_during_open_hours: user.staffing_during_open_hours || false,
        curation_philosophy: user.curation_philosophy || '',
        hosting_motivation: user.hosting_motivation || '',
        hosting_outcome_goals: user.hosting_outcome_goals || '',
        venue_images: user.venue_images || []
      }));
    }
  }, [user]);

  useEffect(() => {
    setExpandedSections({
      venueDescription: false,
      displaySpace: false,
    });
  }, [user?.id]);

  const agreementConfig = useMemo(() => {
    const isGalleryHost = user?.venue_type === 'art_gallery';
    if (isGalleryHost) {
      return {
        agreementCode: "GALLERY_MEMBERSHIP",
        title: "Gallery Host Membership Agreement",
        workflowContext: { workflow: "gallery_host_profile", promo_code: "EARLY_GALLERY_HOST6_12PM_PLATFORM" }
      };
    }
    return {
      agreementCode: "HOST_MEMBERSHIP",
      title: "ARTflow Host Membership Agreement",
      workflowContext: { workflow: "artflow_host_profile", promo_code: "EARLY_ARTFLOW_HOST6_6PM_PLATFORM" }
    };
  }, [user?.venue_type]);

  useEffect(() => {
    if (!isSelf) return;
    let isActive = true;

    const checkAgreement = async () => {
      try {
        const res = await getAgreementStatus([agreementConfig.agreementCode]);
        const row = res.results?.find(item => item.code === agreementConfig.agreementCode);
        if (isActive) {
          setAgreementAccepted(!!row?.accepted);
          setAgreementCheckError(null);
        }
      } catch (err) {
        console.error('Failed to check host agreement status:', err);
        if (isActive) {
          setAgreementAccepted(false);
          setAgreementCheckError('Unable to verify host agreement status.');
        }
      }
    };

    checkAgreement();

    return () => {
      isActive = false;
    };
  }, [isSelf, agreementConfig.agreementCode, user?.id]);

  // --- Profile Images: load from /api/accounts/profile-images/ ---
  const loadProfileImages = () => {
    http.get('/api/accounts/profile-images/')
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : [];
        // Normalize so every item has a `url` field
        const normalized = list.map(pi => ({
          ...pi,
          url: pi.url || pi.image || pi.image_url || null,
        }));
        setProfileImages(normalized);
      })
      .catch((e) => {
        console.warn('Failed to load profile images (host)', e);
        setProfileImages([]);
      });
  };

  useEffect(() => {
    if (!isSelf || !user?.id) return;
    loadProfileImages();
  }, [isSelf, user?.id]);

  useEffect(() => {
    if (!open || !isSelf) return;
    loadProfileImages();
  }, [open, isSelf]);

  const primaryImageUrl = useMemo(() => {
    const fromCard = user?.primary_profile_image_url ? toRelativeMedia(user.primary_profile_image_url) : null;
    if (fromCard) return fromCard;
    const fromUser = user?.primary_profile_image ? toRelativeMedia(user.primary_profile_image) : null;
    if (fromUser) return fromUser;
    if (!isSelf && Array.isArray(user?.profile_images) && user.profile_images.length) {
      const primary = user.profile_images.find((pi) => pi?.is_primary) || user.profile_images[0];
      const src = primary?.url || primary?.image || primary?.image_url;
      return src ? toRelativeMedia(src) : null;
    }
    if (!isSelf) return null;
    const p = profileImages.find(pi => pi.is_primary);
    const src = p?.url || p?.image || p?.image_url;
    return src ? toRelativeMedia(src) : null;
  }, [user?.primary_profile_image_url, user?.primary_profile_image, user?.profile_images, profileImages, isSelf]);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Form handlers
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
  };

  const handleMultiChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: typeof value === 'string' ? value.split(',') : value });
  };

  // Venue image upload
  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);

    for (const file of files) {
      const formDataImg = new FormData();
      console.log('Uploading image file:', file);
      formDataImg.append('image', file);
      formDataImg.append('short_description', '');

      try {
        const res = await http.post(
          `/hosts/${user.id}/upload_venue_image/`,
          formDataImg,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        const uploaded = res?.data;
        console.log('Image upload response:', uploaded);
        if (!uploaded) {
          console.warn('No data returned from upload; skipping state update');
          continue;
        }

        setFormData((prev) => ({
          ...prev,
          venue_images: [...(prev.venue_images || []), uploaded].filter(Boolean),
        }));

        setSnackbar({ open: true, message: 'Image uploaded successfully.', severity: 'success' });
      } catch (err) {
        console.error('Image upload failed', err?.response?.data || err);
        setSnackbar({ open: true, message: 'Failed to upload image.', severity: 'error' });
      }
    }
  };

  const handleImageDelete = async (imageId) => {
    try {
      await http.delete(`/hosts/delete_image/${imageId}/`);
      setFormData(prev => ({
        ...prev,
        venue_images: prev.venue_images.filter(img => img.id !== imageId)
      }));
      setSnackbar({ open: true, message: 'Image deleted.', severity: 'success' });
    } catch (err) {
      console.error('Failed to delete image', err);
      setSnackbar({ open: true, message: 'Failed to delete image.', severity: 'error' });
    }
  };

  const handleDescriptionChange = async (imageId, value) => {
    setFormData(prev => ({
      ...prev,
      venue_images: prev.venue_images.map(img => img.id === imageId ? { ...img, short_description: value } : img)
    }));

    try {
      await http.patch(`/hosts/update_image_description/${imageId}/`, { short_description: value });
      setSnackbar({ open: true, message: 'Description updated.', severity: 'success' });
    } catch (err) {
      console.error('Failed to update image description', err);
      setSnackbar({ open: true, message: 'Failed to update description.', severity: 'error' });
    }
  };

  const handleSave = () => {
    http.put(`/hosts/${user.id}/`, {
      user: {
        first_name: formData.first_name,
        last_name: formData.last_name
      },
      address: formData.address,
      venue_type: formData.venue_type,
      venue_description: formData.venue_description,
      display_space_details: formData.display_space_details,
      lighting_environment_control: formData.lighting_environment_control,
      art_preferences: formData.art_preferences,
      past_art_events: formData.past_art_events,
      foot_traffic_estimate: formData.foot_traffic_estimate,
      artwork_display_availability: formData.artwork_display_availability,
      staffing_during_open_hours: formData.staffing_during_open_hours,
      curation_philosophy: formData.curation_philosophy,
      hosting_motivation: formData.hosting_motivation,
      hosting_outcome_goals: formData.hosting_outcome_goals,
      venue_images: formData.venue_images
    })
      .then(() => {
        handleClose();
        setSnackbar({ open: true, message: 'Profile updated successfully.', severity: 'success' });
      })
      .catch(err => {
        console.error('Failed to update profile', err);
        setSnackbar({ open: true, message: 'Failed to update profile.', severity: 'error' });
      });
  };

  // Viewer data for venue images
  const venueImagesSource = isSelf ? formData.venue_images : (user?.venue_images || []);
  const venueViewerImages = (venueImagesSource || [])
    .filter(Boolean)
    .map((img) => ({
      src: toRelativeMedia(img.image || img.url || img.image_url),
      caption: img.short_description || 'Venue image',
    }));

  // Viewer data for profile images (use url OR image)
  const profileImagesSource = isSelf ? profileImages : (Array.isArray(user?.profile_images) ? user.profile_images : []);
  const profileViewerImages = profileImagesSource
    .map((pi) => {
      const src = toRelativeMedia(pi.url || pi.image || pi.image_url);
      if (!src) return null;
      return { src, caption: 'Profile' };
    })
    .filter(Boolean);

  // Slider images: venue images only (no profile images)
  const sliderImages = useMemo(() => {
    const slides = [...venueViewerImages];

    const seen = new Set();
    return slides.filter((img) => {
      if (!img?.src) return false;
      if (seen.has(img.src)) return false;
      seen.add(img.src);
      return true;
    });
  }, [venueViewerImages]);

  const sliderSettings = {
    dots: true,
    infinite: sliderImages.length > 1,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    adaptiveHeight: true,
    autoplay: sliderImages.length > 1,
    autoplaySpeed: 4000,
    pauseOnHover: true,
    pauseOnFocus: true,
    arrows: true,
    afterChange: (current) => setSliderDotIndex(current),
    appendDots: () => {
      const total = sliderImages.length;
      const visibleCount = Math.min(total, MAX_VISIBLE_SLIDER_DOTS);
      const start = Math.floor(sliderDotIndex / visibleCount) * visibleCount;
      const end = start + visibleCount;

      return (
        <Box
          sx={{
            mt: 1.5,
            px: 1,
            display: 'flex',
            justifyContent: 'center',
            overflow: 'hidden',
            width: '100%',
            '& .slick-active button::before': {
              color: '#111827',
              opacity: 0.95,
            },
            '& li button::before': {
              color: 'rgba(17, 24, 39, 0.45)',
              opacity: 1,
            },
          }}
        >
          <Box
            component="ul"
            sx={{
              m: 0,
              p: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '2px',
              listStyle: 'none',
              maxWidth: '100%',
            }}
          >
            {Array.from({ length: end - start }, (_, offset) => {
              const index = start + offset;
              const isActive = index === sliderDotIndex;
              return (
                <Box
                  key={index}
                  component="li"
                  sx={{ width: 18, display: 'flex', justifyContent: 'center' }}
                >
                  <Box
                    component="button"
                    type="button"
                    aria-label={`Go to slide ${index + 1}`}
                    onClick={() => sliderRef.current?.slickGoTo(index)}
                    sx={{
                      width: isActive ? 12 : 9,
                      height: isActive ? 12 : 9,
                      p: 0,
                      border: 0,
                      borderRadius: '50%',
                      cursor: 'pointer',
                      backgroundColor: isActive ? '#111827' : 'rgba(255, 255, 255, 0.18)',
                      boxShadow: isActive
                        ? '0 0 0 2px rgba(255, 255, 255, 0.9), 0 0 0 4px rgba(17, 24, 39, 0.2)'
                        : 'inset 0 0 0 1px rgba(17, 24, 39, 0.35)',
                      transition: 'background-color 160ms ease, transform 160ms ease, box-shadow 160ms ease, width 160ms ease, height 160ms ease',
                      transform: isActive ? 'scale(1)' : 'scale(0.92)',
                    }}
                  />
                </Box>
              );
            })}
          </Box>
        </Box>
      );
    }
  };

  // Upload/makePrimary/delete for profile images (same as ArtistProfileCard pattern)
  const onUploadProfileImages = async (files) => {
    if (!files?.length) return;
    const remaining = 3 - profileImages.length;
    const toSend = Array.from(files).slice(0, remaining);

    for (let i = 0; i < toSend.length; i++) {
      const fd = new FormData();
      fd.append('image', toSend[i]);
      if (!profileImages.some(p => p.is_primary) && i === 0) {
        fd.append('is_primary', 'true');
      }
      try {
        const { data } = await http.post('/api/accounts/profile-images/', fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        const normalized = {
          ...data,
          url: data.url || data.image || data.image_url || null,
        };
        setProfileImages((prev) => [normalized, ...prev]);
      } catch (e) {
        console.error('Host profile image upload failed', e);
      }
    }
  };

  const makePrimaryProfileImage = async (id) => {
    try {
      const { data } = await http.post(`/api/accounts/profile-images/${id}/make_primary/`);
      setProfileImages((prev) =>
        prev.map((p) => ({ ...p, is_primary: p.id === data.id }))
      );
    } catch (e) {
      console.error('Host set primary profile image failed', e);
    }
  };

  const deleteProfileImage = async (id) => {
    try {
      await http.delete(`/api/accounts/profile-images/${id}/`);
      setProfileImages((prev) => prev.filter((p) => p.id !== id));
    } catch (e) {
      console.error('Host delete profile image failed', e);
    }
  };

  const venueTypeLabel = useMemo(
    () => VENUE_TYPE_OPTIONS.find((option) => option.value === user?.venue_type)?.label || user?.venue_type || '–',
    [user?.venue_type]
  );
  const hostPublicProfileHref = user?.id ? `/hosts/${user.id}` : null;

  return (
    <>
      <Card
        data-testid="host-profile-card"
        sx={{
          width: { xs: '100%', sm: 360, md: 360, lg: 360 },
          maxWidth: { xs: '100%', sm: 360, md: 360, lg: 360 },
          height: 760,
          mb: 3,
          mx: 'auto',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: 3,
          borderRadius: 3,
          backgroundColor: 'rgba(255, 255, 255, 0.6)',
          backdropFilter: 'blur(16px)'
        }}
      >
        {/* Header slider: venue images only */}
        {sliderImages.length > 0 ? (
          <Box
            sx={{
              p: 2,
              pb: 4,
              '& .slick-dots': {
                bottom: -28,
              },
            }}
          >
            <Slider ref={sliderRef} {...sliderSettings}>
              {sliderImages.map((img, idx) => (
                <Box
                  key={idx}
                  sx={{ height: 300, position: 'relative', overflow: 'hidden', cursor: 'zoom-in' }}
                  onClick={() => openViewerWithImages(sliderImages, idx)}
                >
                  {/* Blurred background */}
                  <Box
                    component="img"
                    src={img.src}
                    alt=""
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(20px)',
                      transform: 'scale(1.1)',
                      opacity: 0.3
                    }}
                  />
                  {/* Main contained image */}
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.caption || 'Image'}
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      margin: '0 auto',
                      height: '100%'
                    }}
                  />
                  {img.caption && (
                    <Typography
                      variant="caption"
                      display="block"
                      align="center"
                      sx={{
                        position: 'absolute',
                        bottom: 8,
                        left: 0,
                        right: 0,
                        backgroundColor: 'rgba(0,0,0,0.4)',
                        color: '#fff',
                        p: 0.5
                      }}
                    >
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Slider>
          </Box>
        ) : (
          <Box sx={{ p: 2, pb: 4 }}>
            <Box
              sx={{
                height: 300,
                borderRadius: 2,
                border: '1px solid rgba(148,163,184,0.38)',
                background:
                  'linear-gradient(135deg, rgba(248,250,252,0.9) 0%, rgba(226,232,240,0.9) 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#64748b',
                boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, letterSpacing: 0.2 }}>
                No venue images yet
              </Typography>
            </Box>
          </Box>
        )}

        <CardContent sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {/* Host profile image (same behavior as ArtistProfileCard) */}
              {primaryImageUrl ? (
                <Box
                  component="img"
                  src={primaryImageUrl}
                  alt="Host profile"
                  referrerPolicy="no-referrer"
                  onClick={() => {
                    if (profileViewerImages.length) {
                      const primaryIdx = profileImages.findIndex(pi => pi.is_primary);
                      const startIdx = primaryIdx >= 0 ? primaryIdx : 0;
                      openViewerWithImages(profileViewerImages, startIdx);
                    } else {
                      openViewerWithImages([{ src: primaryImageUrl, caption: 'Profile' }], 0);
                    }
                  }}
                  style={{
                    width: 64,
                    height: 64,
                    objectFit: 'cover',
                    borderRadius: '50%',
                    marginRight: 12,
                    border: '1px solid #e5e7eb',
                    cursor: 'zoom-in'
                  }}
                />
              ) : (
                <Box
                  sx={{
                    width: 64,
                    height: 64,
                    mr: 2,
                    borderRadius: '50%',
                    border: '1px solid rgba(148,163,184,0.4)',
                    background:
                      'linear-gradient(135deg, rgba(248,250,252,0.95) 0%, rgba(226,232,240,0.95) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#64748b',
                    boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.55)',
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="caption" sx={{ fontWeight: 700, textAlign: 'center', lineHeight: 1.2 }}>
                    No
                    <br />
                    Image
                  </Typography>
                </Box>
              )}

              <Box>
                <Typography
                  data-testid="host-profile-name"
                  variant="h6"
                  component={hostPublicProfileHref ? RouterLink : "h2"}
                  to={hostPublicProfileHref || undefined}
                  sx={{
                    color: "inherit",
                    textDecoration: hostPublicProfileHref ? "underline" : "none",
                    textDecorationColor: "rgba(17, 24, 39, 0.35)",
                    textUnderlineOffset: "0.14em",
                    "&:hover": hostPublicProfileHref
                      ? {
                          color: "primary.main",
                          textDecorationColor: "currentColor",
                        }
                      : undefined,
                  }}
                >
                  {user.display_name || `${user.first_name || ''} ${user.last_name || ''}`.trim()}
                </Typography>
                {/* Host engagement summary */}
                {user?.id && (
                  <div style={{ marginTop: 8 }}>
                    <EngagementSummary
                      target="host"
                      objectId={user.id}
                      initialSummary={user.engagement_summary}
                    />
                  </div>
                )}
                <Typography data-testid="host-profile-email" variant="body2" color="text.secondary">{user.email}</Typography>
                <Typography variant="body2" color="text.secondary">{user.role}</Typography>
                {isAuthenticated && !isSelf && chatTarget && (
                  <Button
                    variant="contained"
                    size="small"
                    sx={{
                      mt: 1.25,
                      backgroundColor: '#e5e7eb',
                      color: '#111827',
                      boxShadow: 'none',
                      '&:hover': {
                        backgroundColor: '#d1d5db',
                        boxShadow: 'none',
                      },
                    }}
                    onClick={handleMessageClick}
                  >
                    Message
                  </Button>
                )}
                {isSelf && agreementAccepted === false && (
                  <Box sx={{ mt: 1 }}>
                    <Alert
                      severity={agreementCheckError ? "error" : "warning"}
                      action={(
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setShowAgreementModal(true)}
                        >
                          Review
                        </Button>
                      )}
                    >
                      {agreementCheckError || `Accept the ${agreementConfig.title} to manage your host profile.`}
                    </Alert>
                  </Box>
                )}
                <Typography data-testid="host-profile-venue-type" variant="body2" color="text.secondary"><strong>Venue Type:</strong> {venueTypeLabel}</Typography>
                <Typography data-testid="host-profile-art-preferences" variant="body2" color="text.secondary"><strong>Art Preferences:</strong> {user.art_preferences?.join(', ')}</Typography>
                <Box sx={{ mt: 1.5 }} />
                <ExpandableTextSection
                  title="Venue Description"
                  open={expandedSections.venueDescription}
                  onToggle={() => setExpandedSections((prev) => ({ ...prev, venueDescription: !prev.venueDescription }))}
                  children={(
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        <strong>About:</strong> {user.venue_description || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Curation Philosophy:</strong> {user.curation_philosophy || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Motivation:</strong> {user.hosting_motivation || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Goals:</strong> {user.hosting_outcome_goals || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Past Events:</strong> {user.past_art_events?.join(', ') || '–'}
                      </Typography>
                    </Box>
                  )}
                />
                <ExpandableTextSection
                  title="Display Space"
                  open={expandedSections.displaySpace}
                  onToggle={() => setExpandedSections((prev) => ({ ...prev, displaySpace: !prev.displaySpace }))}
                  children={(
                    <Box sx={{ mt: 1 }}>
                      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                        <strong>Space Description:</strong> {user.display_space_details || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Lighting & Environment Control:</strong> {user.lighting_environment_control?.join(', ') || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Foot Traffic (estimate):</strong> {user.foot_traffic_estimate || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Availability:</strong> {user.artwork_display_availability || '–'}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75, lineHeight: 1.6 }}>
                        <strong>Staffing:</strong> {user.staffing_during_open_hours ? 'Yes' : 'No'}
                      </Typography>
                    </Box>
                  )}
                />
                {user.address && (
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
                    <LocationOnIcon fontSize="small" color="action" sx={{ mr: 0.5 }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.address}
                    </Typography>
                  </Box>
                )}
                <br />
                {isSelf && (
                  <Button
                    variant="outlined"
                    startIcon={<EditIcon />}
                    size="small"
                    onClick={handleOpen}
                  >
                    Edit
                  </Button>
                )}

                {/* Host comments */}
                {user?.id && (
                  <CollapsibleComments target="host" objectId={user.id} />
                )}
              </Box>
            </Box>
          </Box>
          <Divider sx={{ mt: 2 }} />
        </CardContent>
      </Card>

      {/* EDIT MODAL */}
      <Modal open={open} onClose={handleClose}>
        <Box sx={{
          position: 'absolute',
          top: { xs: '50%', sm: '50%' },
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '92vw', sm: 500 },
          maxHeight: { xs: '90vh', sm: '90vh' },
          overflowY: 'auto',
          overflowX: 'hidden',
          bgcolor: 'background.paper',
          p: { xs: 2, sm: 4 },
          boxShadow: 24,
          borderRadius: 2
        }}>
          <Typography variant="h6" mb={2}>Edit Profile</Typography>
          <Stack spacing={2}>
            {/* Profile images manager */}
            <ProfileImageManager
              profileImages={profileImages}
              maxImages={3}
              onUpload={onUploadProfileImages}
              onMakePrimary={makePrimaryProfileImage}
              onDelete={deleteProfileImage}
              onThumbnailClick={(idx) => {
                if (!profileViewerImages.length) return;
                openViewerWithImages(profileViewerImages, idx);
              }}
            />

            <TextField
              label="First Name"
              name="first_name"
              value={formData.first_name}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Last Name"
              name="last_name"
              value={formData.last_name}
              onChange={handleChange}
              fullWidth
            />
            <TextField
              label="Email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              fullWidth
              disabled
            />
            <TextField
              label="Address"
              name="address"
              value={formData.address}
              onChange={handleChange}
              fullWidth
            />
            <FormControl fullWidth>
              <InputLabel id="host-edit-venue-type-label">Venue Type</InputLabel>
              <Select
                labelId="host-edit-venue-type-label"
                label="Venue Type"
                name="venue_type"
                value={formData.venue_type}
                onChange={handleChange}
                fullWidth
              >
                {VENUE_TYPE_OPTIONS.map((option) => (
                  <MenuItem
                    key={option.value}
                    value={option.value}
                    disabled={formData.venue_type === 'art_gallery' && option.value !== 'art_gallery'}
                  >
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Venue Description"
              name="venue_description"
              value={formData.venue_description}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Display Space Details"
              name="display_space_details"
              value={formData.display_space_details}
              onChange={handleChange}
              fullWidth
            />

            <InputLabel>Lighting & Environment Control</InputLabel>
            <Select
              multiple
              name="lighting_environment_control"
              value={formData.lighting_environment_control}
              onChange={handleMultiChange}
              input={<OutlinedInput fullWidth />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (<Chip key={val} label={val} />))}
                </Box>
              )}
              fullWidth
            >
              {LIGHTING_ENVIRONMENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>

            <InputLabel>Art Preferences</InputLabel>
            <Select
              multiple
              name="art_preferences"
              value={formData.art_preferences}
              onChange={handleMultiChange}
              input={<OutlinedInput fullWidth />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (<Chip key={val} label={val} />))}
                </Box>
              )}
              fullWidth
            >
              {ART_TYPE_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>

            <InputLabel>Past Art Events</InputLabel>
            <Select
              multiple
              name="past_art_events"
              value={formData.past_art_events}
              onChange={handleMultiChange}
              input={<OutlinedInput fullWidth />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((val) => (<Chip key={val} label={val} />))}
                </Box>
              )}
              fullWidth
            >
              {ART_EVENT_OPTIONS.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>

            <InputLabel>Foot Traffic Estimate</InputLabel>
            <Select
              name="foot_traffic_estimate"
              value={formData.foot_traffic_estimate}
              onChange={handleChange}
              fullWidth
            >
              {FOOT_TRAFFIC_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>

            <InputLabel>Availability for Artwork Display</InputLabel>
            <Select
              name="artwork_display_availability"
              value={formData.artwork_display_availability}
              onChange={handleChange}
              fullWidth
            >
              {DISPLAY_AVAILABILITY_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>

            <FormControlLabel
              control={
                <Checkbox
                  name="staffing_during_open_hours"
                  checked={formData.staffing_during_open_hours}
                  onChange={handleChange}
                />
              }
              label="Staffing Available During Open Hours"
            />

            <TextField
              label="Curation Philosophy"
              name="curation_philosophy"
              value={formData.curation_philosophy}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Motivation to Host"
              name="hosting_motivation"
              value={formData.hosting_motivation}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Outcome Goals"
              name="hosting_outcome_goals"
              value={formData.hosting_outcome_goals}
              onChange={handleChange}
              fullWidth
              multiline
              rows={2}
            />

            {/* Venue images section (unchanged behavior, but uses generic viewer on click) */}
            <InputLabel>Upload Venue Images</InputLabel>
            <input type="file" multiple onChange={handleImageUpload} accept="image/*" />
            {formData.venue_images?.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {formData.venue_images.map((img, idx) => (
                  <Box key={img.id} sx={{ position: 'relative' }}>
                    <img
                      src={toRelativeMedia(img.image)}
                      alt="Venue"
                      width={80}
                      height={80}
                      style={{ objectFit: 'cover', borderRadius: 4, cursor: 'zoom-in' }}
                      onClick={() => openViewerWithImages(venueViewerImages, idx)}
                    />
                    <TextField
                      size="small"
                      placeholder="Short description"
                      value={img.short_description || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFormData(prev => ({
                          ...prev,
                          venue_images: prev.venue_images.map(i => i.id === img.id ? { ...i, short_description: value } : i)
                        }));
                      }}
                      onBlur={(e) => handleDescriptionChange(img.id, e.target.value)}
                      sx={{ mt: 0.5 }}
                    />
                    <IconButton
                      size="small"
                      sx={{ position: 'absolute', top: -8, right: -8 }}
                      onClick={() => handleImageDelete(img.id)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={handleClose} sx={{ mr: 1 }}>Cancel</Button>
              <Button variant="contained" onClick={handleSave}>Save</Button>
            </Box>
          </Stack>
        </Box>
      </Modal>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {isSelf && (
        <AgreementAcceptModal
          open={showAgreementModal}
          onClose={() => setShowAgreementModal(false)}
          agreementCode={agreementConfig.agreementCode}
          title={agreementConfig.title}
          workflowContext={agreementConfig.workflowContext}
          onAccepted={() => {
            setAgreementAccepted(true);
            setShowAgreementModal(false);
          }}
        />
      )}

      {/* Generic viewer modal for profile OR venue images */}
      <Modal open={viewerOpen} onClose={closeViewer}>
        <Box sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.9)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}>
          <Box sx={{ width: { xs: '95vw', md: '80vw' }, maxWidth: 1200 }}>
            <Slider dots arrows infinite initialSlide={viewerIndex} adaptiveHeight>
              {viewerImages.map((img, i) => (
                <Box
                  key={i}
                  sx={{ height: { xs: '70vh', md: '80vh' }, position: 'relative', overflow: 'hidden' }}
                >
                  {/* blurred backdrop */}
                  <Box
                    component="img"
                    src={img.src}
                    alt=""
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      filter: 'blur(22px)',
                      transform: 'scale(1.1)',
                      opacity: 0.25
                    }}
                  />
                  {/* contained main image */}
                  <Box
                    component="img"
                    src={img.src}
                    alt={img.caption || 'Image'}
                    sx={{
                      position: 'relative',
                      maxWidth: '100%',
                      maxHeight: '100%',
                      objectFit: 'contain',
                      display: 'block',
                      m: '0 auto',
                      height: '100%'
                    }}
                  />
                  {img.caption && (
                    <Typography
                      variant="body2"
                      align="center"
                      sx={{
                        position: 'absolute',
                        bottom: 12,
                        left: 0,
                        right: 0,
                        color: '#fff',
                        bgcolor: 'rgba(0,0,0,0.35)',
                        px: 1,
                        py: 0.5
                      }}
                    >
                      {img.caption}
                    </Typography>
                  )}
                </Box>
              ))}
            </Slider>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Button variant="outlined" onClick={closeViewer}>Close</Button>
            </Box>
          </Box>
        </Box>
      </Modal>
    </>
  );
}
